/* eslint-disable max-lines */
import React, { FunctionComponent, useContext } from 'react';
import { Box, IconButton } from '@wix/design-system';
import { useExperiments } from '@wix/fe-essentials-standalone';
import { TsUtils } from '@wix/answers-api';
import AddSmall from 'wix-ui-icons-common/AddSmall';
import {
  Label,
  RoadmapETAorVotedFilter,
  RoadmapFilter,
  SortAndFilterBiMetaData,
} from '../../../types';
import { RoadmapSortAndFilter } from './../RoadmapSortAndFilter';
import { DATA_HOOKS } from '../../../dataHooks';
import { WalkthroughPopover } from './../WalkthroughPopover';
import { isMobile } from '../../../utils/userAgent';
import { Context } from '../../../context';
import { ApplySortAndFilterFn, WalkthroughLevels } from '..';
import { EXPERIMENTS } from '../../../constants';
import css from './index.module.scss';
import { useRoadmapHeaderBi } from '../RoadmapHeader/bi';

export type RoadmapFiltersHeaderProps = {
  roadmapFilter: RoadmapFilter;
  onChangeRoadmapFilter: (filter: RoadmapFilter) => void;
  labels: Label[];
  apply: ApplySortAndFilterFn;
  onSearch: (text: string) => void;
  isScrolled: boolean;
  onChangeTriggerBiMetaData: (
    triggerBiMetaData: SortAndFilterBiMetaData
  ) => void;
  walkthroughStep: WalkthroughLevels | null;
  setWalkthroughStep: (walkthroughLevel: WalkthroughLevels | null) => void;
  setShowDesktopOnlyModal: (showDesktopOnlyModal: boolean) => void;
  locale: string;
  setFeatureRequestModalVisible: (featureREquestModalVisible: boolean) => Promise<void>;
};

export const allFilterOptions = TsUtils.enumToArray(
  RoadmapETAorVotedFilter
) as RoadmapETAorVotedFilter[];

export const RoadmapFiltersHeader: FunctionComponent<
  RoadmapFiltersHeaderProps
> = ({
  roadmapFilter,
  onChangeRoadmapFilter,
  labels,
  apply,
  isScrolled,
  onChangeTriggerBiMetaData,
  walkthroughStep,
  setWalkthroughStep,
  setShowDesktopOnlyModal,
  setFeatureRequestModalVisible
}) => {
  const context = useContext(Context);
  const { isWixStudioUser } = context;
  const isMobileView = isMobile(context);
  const { experiments } = useExperiments({ readOnly: true });
  const biLogger = useRoadmapHeaderBi();

  const onClickFeatureRequest = async () => {
    if (isMobileView && experiments.enabled(EXPERIMENTS.SPECS.IMPROVE_SIMILARITY_MECHANISM)) {
      biLogger.onRequestFeatureClickFromMobile();
      setShowDesktopOnlyModal(true);
    } else {
      if (experiments.enabled(EXPERIMENTS.SPECS.IMPROVE_SIMILARITY_MECHANISM)) {
      biLogger.onRequestFeatureClick();
      }
      await setFeatureRequestModalVisible(true);
    }
  };

  return (
    <Box
      className={css.roadmapFilterHeader}
      align={isMobileView ? 'right' : undefined}
    >
      <RoadmapSortAndFilter
        roadmapFilter={roadmapFilter}
        onChangeFilter={onChangeRoadmapFilter}
        productLabels={labels}
        applyValue={apply}
        isScrolled={isScrolled}
        walkthroughStep={walkthroughStep || undefined}
        onChangeWalkthroughStep={(walkthrough: WalkthroughLevels | null) =>
          setWalkthroughStep(walkthrough)
        }
        onChangeTriggerBiMetaData={onChangeTriggerBiMetaData}
      />
      {isWixStudioUser && isMobileView && (
        <WalkthroughPopover
          walkthroughLevels={WalkthroughLevels.FILTERS}
          opened={walkthroughStep === WalkthroughLevels.FILTERS}
          placement="bottom"
          onClose={() => setWalkthroughStep(null)}
          onChangeLevel={(walkthroughLevels: WalkthroughLevels) =>
            setWalkthroughStep(walkthroughLevels)
          }
        >
          <IconButton
            onClick={onClickFeatureRequest}
            className={css.requestFeatureIconButton}
            dataHook={DATA_HOOKS.ROADMAP_FEATURE_REQUEST_BUTTON}
            priority="secondary"
            size="tiny"
          >
            <AddSmall height={'11px'} width={'11px'} />
          </IconButton>
        </WalkthroughPopover>
      )}
    </Box>
  );
};
